import { useState, useEffect } from 'react';
import api from '../services/api';

const STYLES = [
  { key: 'carousel', label: 'Carousel', desc: 'One review at a time with arrows' },
  { key: 'grid', label: 'Grid', desc: 'Multi-card masonry layout' },
  { key: 'strip', label: 'Strip', desc: 'Compact trust bar' },
];

const THEMES = [
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
  { key: 'minimal', label: 'Minimal' },
];

export default function ReviewWidgetSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [style, setStyle] = useState('carousel');
  const [theme, setTheme] = useState('light');
  const [maxReviews, setMaxReviews] = useState(5);
  const [showDates, setShowDates] = useState(true);
  const [minRating, setMinRating] = useState(4);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await api.get('/widget/config');
        setConfig(data);
        setStyle(data.style || 'carousel');
        setTheme(data.theme || 'light');
        setMaxReviews(data.max_reviews || 5);
        setShowDates(data.show_dates !== false);
        setMinRating(data.min_rating || 4);
      } catch { }
      finally { setLoading(false); }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.post('/widget/config', {
        style, theme, max_reviews: maxReviews, show_dates: showDates, min_rating: minRating,
      });
      setConfig(data);
      setSuccess('Widget settings saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save: ' + (err.message || 'Unknown error'));
    } finally { setSaving(false); }
  };

  const handleCopy = () => {
    if (config?.embed_code) {
      navigator.clipboard.writeText(config.embed_code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  if (loading) return null;
  const hasSlug = !!config?.slug;

  return (
    <div>
      <hr className="settings-divider" />
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Review Widget</h2>
      <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
        Embed your best reviews on your website. Auto-updates as new reviews come in.
      </p>

      {!hasSlug && (
        <div style={{ padding: '16px 20px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: '#92400e', margin: 0 }}>
            <strong>Set up your Review Request Link first.</strong> The widget uses the same slug to identify your business.
          </p>
        </div>
      )}

      {hasSlug && (
        <>
          {/* Embed code */}
          <div style={{ padding: '16px 20px', background: 'rgba(22,163,74,0.04)', border: '1px solid rgba(22,163,74,0.15)', borderRadius: 8, marginBottom: 24 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>Embed Code</label>
            <p style={{ fontSize: 12, color: 'var(--text-light)', margin: '0 0 10px' }}>
              Paste this anywhere on your website to display your review wall.
            </p>
            <div style={{ background: '#1a1a1a', borderRadius: 6, padding: '12px 16px', fontFamily: 'monospace', fontSize: 12.5, color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', position: 'relative' }}>
              {config.embed_code}
              <button className="btn btn-sm" onClick={handleCopy} style={{ position: 'absolute', top: 8, right: 8, fontSize: 11, padding: '4px 10px', background: copied ? '#16a34a' : '#374151', color: '#fff', border: 'none', borderRadius: 4 }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Layout style */}
          <div className="form-group">
            <label className="form-label">Layout Style</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {STYLES.map(s => (
                <button key={s.key} type="button" onClick={() => setStyle(s.key)} style={{
                  flex: 1, padding: '12px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                  border: style === s.key ? '2px solid var(--green)' : '1px solid var(--border)',
                  background: style === s.key ? 'rgba(22,163,74,0.04)' : 'var(--bg)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Theme</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {THEMES.map(t => (
                <button key={t.key} type="button" onClick={() => setTheme(t.key)} style={{
                  flex: 1, padding: '12px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                  border: theme === t.key ? '2px solid var(--green)' : '1px solid var(--border)',
                  background: t.key === 'dark' ? '#1e293b' : t.key === 'minimal' ? '#fafafa' : '#fff',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: t.key === 'dark' ? '#e2e8f0' : '#1a1a1a' }}>{t.label}</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 4 }}>
                    {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ color: s <= 4 ? '#f59e0b' : (t.key === 'dark' ? '#334155' : '#ddd'), fontSize: 12 }}>&#9733;</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
            <div className="form-group">
              <label className="form-label">Max Reviews</label>
              <select className="form-input" value={maxReviews} onChange={e => setMaxReviews(Number(e.target.value))}>
                {[3, 5, 8, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Minimum Rating</label>
              <select className="form-input" value={minRating} onChange={e => setMinRating(Number(e.target.value))}>
                {[3, 4, 5].map(n => <option key={n} value={n}>{n}+ stars</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Show Dates</label>
              <div style={{ paddingTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={showDates} onChange={e => setShowDates(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--green)' }} />
                  Show review dates
                </label>
              </div>
            </div>
          </div>

          {/* Save */}
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Widget Settings'}
            </button>
            {success && <span style={{ fontSize: 13, color: 'var(--green)' }}>{success}</span>}
            {error && <span style={{ fontSize: 13, color: '#dc2626' }}>{error}</span>}
          </div>

          {/* Live Preview */}
          <div style={{ marginTop: 28 }}>
            <label className="form-label" style={{ marginBottom: 12 }}>Live Preview</label>
            <div style={{
              padding: 24, borderRadius: 12,
              border: '1px solid var(--border)',
              background: theme === 'dark' ? '#0f172a' : theme === 'minimal' ? '#fafafa' : '#fff',
              overflow: 'hidden',
            }}>
              <WidgetPreview layout={style} theme={theme} showDates={showDates} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ── Premium Preview Component ────────────────────

const MOCK = [
  { name: 'Sarah M.', rating: 5, text: 'Absolutely wonderful service. The team went above and beyond for us. Could not recommend more highly!', date: '10 Mar 2026' },
  { name: 'James T.', rating: 4, text: 'Great food and lovely atmosphere. Really enjoyed the evening out.', date: '08 Mar 2026' },
  { name: 'Priya K.', rating: 5, text: 'Hidden gem! Best coffee in town. Will definitely be coming back regularly.', date: '01 Mar 2026' },
];

const AVATAR_COLORS = ['#16a34a', '#2563eb', '#9333ea', '#dc2626', '#d97706'];

function getThemeColors(theme) {
  if (theme === 'dark') return {
    bg: '#0f172a', card: '#1e293b', border: '#334155', text: '#e2e8f0',
    muted: '#94a3b8', star: '#fbbf24', starEmpty: '#334155', accent: '#34d399',
    quoteMark: 'rgba(52,211,153,0.15)', shadow: '0 4px 16px rgba(0,0,0,0.3)',
  };
  if (theme === 'minimal') return {
    bg: 'transparent', card: 'transparent', border: '#e5e5e5', text: '#1a1a1a',
    muted: '#888', star: '#f59e0b', starEmpty: '#e5e5e5', accent: '#1a1a1a',
    quoteMark: 'rgba(0,0,0,0.04)', shadow: 'none',
  };
  return {
    bg: '#fff', card: '#fff', border: '#eee', text: '#1a1a1a',
    muted: '#6b7280', star: '#f59e0b', starEmpty: '#e5e5e5', accent: '#16a34a',
    quoteMark: 'rgba(22,163,74,0.06)', shadow: '0 2px 12px rgba(0,0,0,0.06)',
  };
}

function Stars({ rating, c, size = 14 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ fontSize: size, color: i <= rating ? c.star : c.starEmpty }}>&#9733;</span>
      ))}
    </span>
  );
}

function Avatar({ name, size = 36 }) {
  const color = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700, color: '#fff', flexShrink: 0,
    }}>{name[0].toUpperCase()}</div>
  );
}


// ── CAROUSEL: Single review hero with arrows ─────

function CarouselPreview({ theme, showDates }) {
  const [idx, setIdx] = useState(0);
  const c = getThemeColors(theme);
  const r = MOCK[idx];

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % MOCK.length), 4000);
    return () => clearInterval(t);
  }, []);

  const arrowStyle = (side) => ({
    position: 'absolute', top: '50%', [side]: -4,
    transform: 'translateY(-50%)',
    width: 32, height: 32, borderRadius: '50%',
    border: `1px solid ${c.border}`, background: c.card,
    color: c.muted, fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: c.shadow, zIndex: 2,
  });

  return (
    <div style={{ position: 'relative', maxWidth: 480, margin: '0 auto', padding: '0 24px' }}>
      <div style={{ textAlign: 'center', padding: '24px 20px' }}>
        {/* Large quote mark */}
        <div style={{ fontSize: 48, lineHeight: 1, color: c.accent, opacity: 0.25, marginBottom: 8 }}>"</div>

        {/* Review text */}
        <p style={{
          fontSize: 17, lineHeight: 1.7, color: c.text, margin: '0 0 20px',
          fontStyle: 'italic', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto',
          transition: 'opacity 0.3s ease',
        }}>
          "{r.text}"
        </p>

        {/* Stars */}
        <Stars rating={r.rating} c={c} size={18} />

        {/* Author */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 14 }}>
          <Avatar name={r.name} size={32} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: c.text }}>{r.name}</div>
            {showDates && <div style={{ fontSize: 11, color: c.muted }}>{r.date}</div>}
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
          {MOCK.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              width: i === idx ? 20 : 8, height: 8, borderRadius: 4,
              border: 'none', cursor: 'pointer', transition: 'all 0.3s ease',
              background: i === idx ? c.accent : c.border, padding: 0,
            }} />
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button style={arrowStyle('left')} onClick={() => setIdx(i => (i - 1 + MOCK.length) % MOCK.length)}>&#8249;</button>
      <button style={arrowStyle('right')} onClick={() => setIdx(i => (i + 1) % MOCK.length)}>&#8250;</button>

      <Brand c={c} />
    </div>
  );
}


// ── GRID: Card masonry layout ────────────────────

function GridPreview({ theme, showDates }) {
  const c = getThemeColors(theme);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {MOCK.map((r, i) => (
          <div key={i} style={{
            background: theme === 'minimal' ? 'transparent' : c.card,
            border: `1px solid ${c.border}`,
            borderRadius: 12, padding: '18px 16px',
            boxShadow: c.shadow,
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Stars at top */}
            <Stars rating={r.rating} c={c} size={14} />

            {/* Quote */}
            <p style={{
              fontSize: 13, lineHeight: 1.6, color: c.text, margin: '10px 0',
              flex: 1,
            }}>"{r.text}"</p>

            {/* Author row at bottom */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              paddingTop: 10, borderTop: `1px solid ${c.border}`, marginTop: 'auto',
            }}>
              <Avatar name={r.name} size={28} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, color: c.text }}>{r.name}</div>
                {showDates && <div style={{ fontSize: 10, color: c.muted }}>{r.date}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Brand c={c} />
    </div>
  );
}


// ── STRIP: Compact trust bar ─────────────────────

function StripPreview({ theme, showDates }) {
  const c = getThemeColors(theme);

  return (
    <div>
      {/* Summary bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 12, marginBottom: 14, paddingBottom: 14,
        borderBottom: `1px solid ${c.border}`,
      }}>
        <span style={{ fontSize: 28, fontWeight: 800, color: c.accent }}>4.7</span>
        <div>
          <Stars rating={5} c={c} size={16} />
          <div style={{ fontSize: 11, color: c.muted, marginTop: 1 }}>Based on 47 reviews</div>
        </div>
      </div>

      {/* Compact review strips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {MOCK.map((r, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 0',
            borderBottom: i < MOCK.length - 1 ? `1px solid ${c.border}` : 'none',
          }}>
            <Avatar name={r.name} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 12.5, color: c.text }}>{r.name}</span>
                <Stars rating={r.rating} c={c} size={11} />
                {showDates && <span style={{ fontSize: 10, color: c.muted, marginLeft: 'auto', flexShrink: 0 }}>{r.date}</span>}
              </div>
              <p style={{
                fontSize: 12, color: c.muted, margin: '2px 0 0', lineHeight: 1.4,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>"{r.text}"</p>
            </div>
          </div>
        ))}
      </div>
      <Brand c={c} />
    </div>
  );
}

function Brand({ c }) {
  return (
    <div style={{ textAlign: 'center', marginTop: 14, fontSize: 10, color: c.muted }}>
      Powered by <span style={{ fontWeight: 600 }}>StellarReply</span>
    </div>
  );
}


function WidgetPreview({ layout, theme, showDates }) {
  if (layout === 'carousel') return <CarouselPreview theme={theme} showDates={showDates} />;
  if (layout === 'grid') return <GridPreview theme={theme} showDates={showDates} />;
  return <StripPreview theme={theme} showDates={showDates} />;
}
